import { formatDateOnlyForLocale, isValidDateOnly } from "@multi-chat/shared";
import { Cake, X } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover/index.js";
import { cn } from "../../lib/utils.js";
import { contactFormFieldInputClass, contactFormFieldLabelClass } from "./contact-sheet-presentation.js";

const ContactBirthdayCalendar = lazy(() =>
  import("./ContactBirthdayCalendar.js").then((module) => ({ default: module.ContactBirthdayCalendar })),
);

/** Localized calendar-date label; never shifts the day across timezones. */
export function formatContactBirthday(value: string | null | undefined, locale: string): string {
  if (!value || !isValidDateOnly(value)) return "";
  return formatDateOnlyForLocale(value, locale, { dateStyle: "medium" });
}

/** Shared Birthday input for Contact create, Contact edit, and Inbox Add as Contact. */
export function ContactBirthdayField({
  id,
  value,
  disabled,
  onChange,
  invalid,
  describedBy,
}: {
  id: string;
  /** `YYYY-MM-DD`, or empty when unset. */
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  invalid?: boolean;
  describedBy?: string;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const display = formatContactBirthday(value, i18n.language.startsWith("he") ? "he-IL" : "en-US");

  return (
    <div className="grid gap-1" data-testid="contact-birthday-field">
      <label htmlFor={id} className={cn(contactFormFieldLabelClass, "flex items-center gap-1.5")}>
        <Cake size={13} className="shrink-0 text-muted" aria-hidden="true" />
        {t("contacts.birthday.label")}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <div className="relative">
          <PopoverTrigger asChild>
            <button
              id={id}
              type="button"
              disabled={disabled}
              aria-invalid={invalid}
              aria-describedby={describedBy}
              className={cn(
                contactFormFieldInputClass,
                "flex items-center gap-2 text-start",
                value ? "pe-9" : undefined,
              )}
            >
              <Cake size={15} className="shrink-0 text-muted" aria-hidden="true" />
              <span className={cn("min-w-0 flex-1 truncate", !display && "text-muted")}>
                {display || t("contacts.birthday.placeholder")}
              </span>
            </button>
          </PopoverTrigger>
          {value && !disabled ? (
            <button
              type="button"
              onClick={() => onChange("")}
              aria-label={t("contacts.birthday.clear")}
              title={t("contacts.birthday.clear")}
              className="absolute inset-y-0 end-1 my-auto inline-grid size-7 place-items-center rounded-md text-muted hover:bg-surface-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/50"
            >
              <X size={14} aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <PopoverContent align="start" className="w-auto p-3">
          <Suspense fallback={<div className="h-[22rem] w-[20rem]" aria-hidden="true" />}>
            <ContactBirthdayCalendar
              value={value}
              onSelect={(next) => {
                onChange(next);
                setOpen(false);
              }}
            />
          </Suspense>
        </PopoverContent>
      </Popover>
    </div>
  );
}
